/**
 * @file PutStockLotList.tsx
 * @description Второй шаг диалога «Положить товар»: выбор партий фасовки
 * (phase19.23, A4).
 *
 * Выбор множественный: в ячейку кладут пачку партий за один заход, поэтому
 * строки — чекбоксы, а не радио. `"all"` — отдельное значение, а не список id:
 * «все партии» должно остаться «всеми», даже если список партий обновится.
 *
 * Одна партия живёт в одной ячейке (W14), поэтому выбор партии — это выбор
 * того, что переедет. Если партия сейчас лежит в другой ячейке, показываем
 * предупреждение о переносе: кладовщик не должен узнать об этом постфактум.
 */

import { AlertTriangle, Check } from "lucide-react";

import { cn } from "@/lib/utils";
import type { PutStockLot, PutStockSku } from "./usePutStock";

interface Props {
  sku: PutStockSku;
  /** Ячейка назначения — с ней сравниваем текущий адрес партии. */
  targetBinId: string;
  targetBinCode: string;
  /** Выбранные партии (`all` — все партии фасовки). */
  selected: "all" | string[];
  onSelect: (selected: "all" | string[]) => void;
}

/** «L-240901 · до 01.12.2026 · 7 шт» — одна строка партии. */
function lotText(lot: PutStockLot): string {
  const expires = lot.expiresAt
    ? ` · до ${new Date(lot.expiresAt).toLocaleDateString("ru-RU")}`
    : "";
  return `${lot.batchNumber}${expires} · ${lot.quantity.toLocaleString("ru-RU")} шт`;
}

/** Квадратный чекбокс тач-размера: сам `button` уже ≥56px (правила WMS UI). */
function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-md border",
        checked ? "border-primary bg-primary text-on-primary" : "border-on-surface/30",
      )}
    >
      <Check className={cn("size-4", checked ? "opacity-100" : "opacity-0")} />
    </span>
  );
}

export function PutStockLotList({
  sku,
  targetBinId,
  targetBinCode,
  selected,
  onSelect,
}: Props) {
  const allIds = sku.lots.map((l) => l.lotId);
  const isAll = selected === "all";
  const chosen = isAll ? allIds : selected;
  const moving = sku.lots.filter(
    (lot) => chosen.includes(lot.lotId) && lot.binId && lot.binId !== targetBinId,
  );

  /** Снять/поставить галку одной партии; «все» разворачиваем в список. */
  function toggleLot(lotId: string) {
    const next = chosen.includes(lotId)
      ? chosen.filter((id) => id !== lotId)
      : [...chosen, lotId];
    // Полный набор снова сворачиваем в `all` — подпись кнопки должна совпадать.
    onSelect(next.length === allIds.length ? "all" : next);
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => onSelect(isAll ? [] : "all")}
        aria-pressed={isAll}
        className={cn(
          "flex min-h-[56px] items-center gap-3 rounded-xl border px-3 text-left text-sm",
          isAll ? "border-primary bg-primary/5" : "border-on-surface/15",
        )}
      >
        <CheckBox checked={isAll} />
        <span>
          Выбрать все партии
          <span className="block text-xs text-on-surface-variant">
            {sku.lots.length} шт партий · {sku.totalQuantity.toLocaleString("ru-RU")} шт
          </span>
        </span>
      </button>

      <ul className="flex flex-col gap-2">
        {sku.lots.map((lot) => {
          const active = chosen.includes(lot.lotId);
          return (
            <li key={lot.lotId}>
              <button
                type="button"
                onClick={() => toggleLot(lot.lotId)}
                aria-pressed={active}
                className={cn(
                  "flex min-h-[56px] w-full items-center gap-3 rounded-xl border px-3 text-left text-sm",
                  active ? "border-primary bg-primary/5" : "border-on-surface/15",
                )}
              >
                <CheckBox checked={active} />
                <span className="min-w-0">
                  <span className="block truncate font-mono">{lotText(lot)}</span>
                  <span className="block text-xs text-on-surface-variant">
                    {lot.binCode
                      ? lot.binId === targetBinId
                        ? "Уже в этой ячейке"
                        : `Сейчас в ${lot.binCode}`
                      : "Адрес не задан"}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {moving[0] ? (
        <p className="flex items-start gap-2 rounded-xl bg-warning/15 px-3 py-2.5 text-sm text-warning">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            {moving.length === 1
              ? `Партия ${moving[0].batchNumber} переедет из ${moving[0].binCode} в ${targetBinCode}.`
              : `Выбранные партии переедут в ${targetBinCode}: ${moving.length}. Прежние адреса освободятся.`}
          </span>
        </p>
      ) : null}
    </div>
  );
}
